import { runCaptureFirstExtraction } from '../rawCareCaptureExtraction/captureFirstExtractionService';
import { buildTimelineRefreshPayload, buildTimelineRefreshProcessingPayload, buildTimelineRefreshFailedPayload } from './timelineNoteRefreshPayloads';
import { applyCurrentTimelineRefresh } from './timelineRefreshRevision';

const DAILY_LOGS_COLLECTION = 'dailyLogs';

const applyDottedPath = (target, path, value) => {
  const parts = String(path || '').split('.').filter(Boolean);
  if (!parts.length) {
    return;
  }

  let cursor = target;
  parts.forEach((part, index) => {
    if (index === parts.length - 1) {
      cursor[part] = value;
      return;
    }

    if (!cursor[part] || typeof cursor[part] !== 'object' || Array.isArray(cursor[part])) {
      cursor[part] = {};
    }
    cursor = cursor[part];
  });
};

const expandTimelinePatchForLocalState = (payload = {}) => (
  Object.entries(payload || {}).reduce((patch, [key, value]) => {
    if (key.includes('.')) {
      applyDottedPath(patch, key, value);
    } else {
      patch[key] = value;
    }
    return patch;
  }, {})
);

const dispatchTimelineEntryUpdated = ({ childId, entryId, patch }) => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }

  window.dispatchEvent(new CustomEvent('captureez:timeline-entry-updated', {
    detail: {
      childId,
      entryId,
      collection: DAILY_LOGS_COLLECTION,
      patch,
    },
  }));
};

const runTimelineRefreshInBackground = ({
  updatedEntry,
  childId,
  category,
  updatedBy = null,
}) => {
  runCaptureFirstExtraction({
    rawText: updatedEntry.text,
    sourceType: 'typed_long_note',
    options: {
      childId,
      sourceMetadata: {
        sourceContext: 'timeline',
        sourceKind: 'text',
        documentType: 'timeline_edit',
        originalTimelineEntryId: updatedEntry.id,
        selectedLocalDate: updatedEntry.localDate,
        selectedTime: updatedEntry.localTime || null,
        reprocessRequested: true,
        reprocessedBy: updatedBy || null,
      },
    },
  })
    .then((result) => {
      if (result?.ok === false) throw new Error(result.error?.message || 'AI refresh could not finish.');
      const refreshPayload = buildTimelineRefreshPayload({
        updatedEntry,
        extractionResult: result,
        category,
        updatedBy,
      });
      return applyCurrentTimelineRefresh(updatedEntry, refreshPayload).then((applied) => {
        if (!applied) return;
        dispatchTimelineEntryUpdated({
          childId,
          entryId: updatedEntry.id,
          patch: expandTimelinePatchForLocalState(refreshPayload),
        });
      });
    })
    .catch((error) => {
      const failedPayload = buildTimelineRefreshFailedPayload({ updatedBy, error });
      return applyCurrentTimelineRefresh(updatedEntry, failedPayload).then((applied) => {
        if (!applied) return;
        dispatchTimelineEntryUpdated({
          childId,
          entryId: updatedEntry.id,
          patch: expandTimelinePatchForLocalState(failedPayload),
        });
      });
    });
};

export const startTimelineNoteRefresh = async ({ updatedEntry, childId, category, updatedBy }) => {
  try {
    const processingPayload = buildTimelineRefreshProcessingPayload({ updatedBy });
    const applied = await applyCurrentTimelineRefresh(updatedEntry, processingPayload);
    if (!applied) return false;
    dispatchTimelineEntryUpdated({
      childId,
      entryId: updatedEntry.id,
      patch: expandTimelinePatchForLocalState(processingPayload),
    });
  } catch (error) {
    const reprocessError = new Error(error?.message || 'The edit was saved, but AI refresh could not start.');
    reprocessError.editSaved = true;
    reprocessError.updatedEntry = updatedEntry;
    throw reprocessError;
  }

  runTimelineRefreshInBackground({
    updatedEntry,
    childId,
    category,
    updatedBy,
  });

  return true;
};
